// The ERP's "ficha operativa" (Softur report "RECEPTIVO-FICHA OPERATIVA", owner 2026-10-07) turned into the import's
// own CSV. Layout of the report: title rows first (row 21 in the sample), then the header row, one row per passenger,
// and summary rows at the end ("Total CHD:", "Total Inf:"…). Columns used, found BY THEIR TITLE (so a moved column still
// works): "Apellido Pax" (B), "Nombre Pax" (C), "Sexo" (D), "Tipo doc." (E), "Nº de doc." (F) and the email the owner
// adds at the end (M: any title with "correo" or "mail") and, optionally, a "Rol" column (N) where an "X" marks the
// signatory (2026-10-22: the ERP report does not carry one, so the administrator adds that row by hand) and, since 2026-10-28 (owner's
// decision: the hotel's rooming list carries it), "Fecha de Nacimiento" (G; Excel serial number or text). Everything else of the report (phone, food
// restrictions…) is NOT read: the system does not need it (data minimization). The server validates the result as any
// other import.

const normalize = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

type Field = 'apellidos' | 'nombres' | 'sexo' | 'tipo' | 'documento' | 'correo' | 'rol' | 'nacimiento';

function fieldOf(title: string): Field | undefined {
  const t = normalize(title);
  if (t === 'apellido pax' || t === 'apellidos pax' || t === 'apellido' || t === 'apellidos') return 'apellidos';
  if (t === 'nombre pax' || t === 'nombres pax' || t === 'nombre' || t === 'nombres') return 'nombres';
  if (t === 'sexo') return 'sexo';
  if (t === 'tipo doc' || t === 'tipo documento' || t === 'tipo de documento') return 'tipo';
  // "Nº de doc." (º becomes "o"), "N° de doc.", "Nro. de doc.", "Número de documento"…
  if (/^(n|no|nro|numero)( de)? doc(umento)?$/.test(t)) return 'documento';
  if (t.includes('correo') || t.includes('mail')) return 'correo';
  // The optional column the administrator adds after the email (N) to mark the signatory (owner, 2026-10-22).
  if (t === 'rol' || t === 'firmante') return 'rol';
  if (t === 'fecha de nacimiento' || t === 'fecha nacimiento' || t === 'nacimiento') return 'nacimiento';
  return undefined;
}

/** A cell of the role column: "X" (or "firmante") marks the group's signatory, "A" a companion (a parent who travels); anything else is a passenger. */
function roleOf(value: string): string {
  const v = value.trim().toLowerCase();
  if (v === 'a' || v === 'acompañante' || v === 'acompanante') return 'acompanante';
  return v === 'x' || v === 'firmante' ? 'firmante' : v === '' || v === 'pasajero' ? 'pasajero' : value.trim();
}

/** A birth-date cell: an Excel serial number (days since 1899-12-30) becomes `yyyy-mm-dd`; text is passed on as is (the server validates it). */
function birthDateOf(value: string): string {
  const v = value.trim();
  if (!/^\d+(\.\d+)?$/.test(v)) return v;
  const serial = Math.floor(Number(v));
  if (serial < 60 || serial > 80_000) return v;
  return new Date(Date.UTC(1899, 11, 30) + serial * 86_400_000).toISOString().slice(0, 10);
}

/** A cell for the import CSV (";"-separated): quoted when needed, never a line break. */
const cell = (value: string): string => {
  const clean = value.replace(/[\r\n\t]+/g, ' ').trim();
  return /[";]/.test(clean) ? `"${clean.replaceAll('"', '""')}"` : clean;
};

export type ErpConversion =
  | { readonly ok: true; readonly csv: string; readonly passengers: number; readonly withEmail: number }
  | { readonly ok: false; readonly reason: string };

/** Finds the header row (the one with "Apellido Pax" and "Nombre Pax") and converts the passenger rows below it. */
export function erpReportToCsv(rows: readonly (readonly string[])[]): ErpConversion {
  const headerIndex = rows.findIndex((row) => {
    const fields = row.map(fieldOf);
    return fields.includes('apellidos') && fields.includes('nombres') && fields.includes('documento');
  });
  if (headerIndex < 0) {
    return { ok: false, reason: 'No se encontró la fila de títulos («Apellido Pax», «Nombre Pax», «Nº de doc.»).' };
  }
  const header = rows[headerIndex] ?? [];
  const column: Partial<Record<Field, number>> = {};
  header.forEach((title, index) => {
    const field = fieldOf(title);
    if (field !== undefined && column[field] === undefined) column[field] = index;
  });
  if (column.sexo === undefined) return { ok: false, reason: 'Falta la columna «Sexo».' };

  const at = (row: readonly string[], field: Field): string => {
    const index = column[field];
    return index === undefined ? '' : (row[index] ?? '').trim();
  };
  // The `rol` column is added to the CSV only when the sheet has one, so a plain ERP report converts exactly as before.
  const withRole = column.rol !== undefined;
  const withBirthDate = column.nacimiento !== undefined;
  const lines = [
    [
      'nombres',
      'apellidos',
      'numero_documento',
      'sexo',
      'tipo_documento',
      'correo',
      ...(withRole ? ['rol'] : []),
      ...(withBirthDate ? ['fecha_nacimiento'] : []),
    ].join(';'),
  ];
  let withEmail = 0;
  for (const row of rows.slice(headerIndex + 1)) {
    const values = (['apellidos', 'nombres', 'sexo', 'documento'] as const).map((field) => at(row, field));
    // The summary rows ("Total CHD:", "Total Inf:"…) end the list, and so does a fully empty row.
    if (values.some((value) => value.endsWith(':'))) break;
    if (values.every((value) => value === '')) break;
    const email = at(row, 'correo');
    if (email !== '') withEmail += 1;
    lines.push(
      [
        at(row, 'nombres'),
        at(row, 'apellidos'),
        at(row, 'documento'),
        at(row, 'sexo'),
        at(row, 'tipo'),
        email,
        ...(withRole ? [roleOf(at(row, 'rol'))] : []),
        ...(withBirthDate ? [birthDateOf(at(row, 'nacimiento'))] : []),
      ]
        .map(cell)
        .join(';'),
    );
  }
  if (lines.length === 1) return { ok: false, reason: 'La planilla no tiene pasajeros debajo de la fila de títulos.' };
  return { ok: true, csv: `${lines.join('\n')}\n`, passengers: lines.length - 1, withEmail };
}

// ── The same sheet as text ────────────────────────────────────────────────────────────────────────────────────────────
// The "ficha operativa" also arrives as a CSV (saved from Excel: ";" on Spanish-locale Windows, "," elsewhere) or pasted
// from Excel (TAB-separated). The server only accepts its own short CSV (at most 6 columns, DR-367), so the browser
// converts the ficha first, exactly as it does with the .xlsx: same columns, same rules; the phone, the food restriction,
// the hotel… are never sent. Text WITHOUT the ficha's own titles ("Apellido Pax" and "Nombre Pax") is left untouched.

/** Splits delimited text (RFC 4180 quotes; BOM ignored) into rows. The delimiter is the most frequent of ; TAB , in the first lines. */
export function delimitedTextToRows(text: string): string[][] {
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const head = clean.split(/\r?\n/).slice(0, 25).join('\n');
  const count = (char: string): number => head.split(char).length - 1;
  const delimiter = [';', '\t', ','].reduce((best, char) => (count(char) > count(best) ? char : best), ';');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < clean.length; i += 1) {
    const char = clean.charAt(i);
    if (quoted) {
      if (char === '"' && clean.charAt(i + 1) === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') quoted = false;
      else field += char;
    } else if (char === '"') quoted = true;
    else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && clean.charAt(i + 1) === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += char;
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Converts pasted or CSV text when it is a ficha operativa (it carries the titles «Apellido Pax» and «Nombre Pax»);
 * returns `undefined` for any other text, which the caller sends as it is.
 */
export function fichaTextToCsv(text: string): ErpConversion | undefined {
  const rows = delimitedTextToRows(text);
  const isFicha = rows.some((row) => {
    const titles = row.map((title) => normalize(title));
    return titles.includes('apellido pax') && titles.includes('nombre pax');
  });
  return isFicha ? erpReportToCsv(rows) : undefined;
}
